import { Suspense, lazy, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import LoginPage from "@/pages/LoginPage";
import { Toaster } from "@/components/ui/sonner";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { buildOverridesCss } from "@/modules/styleEditor/styleUtils";
import { getPublicBasePath, parseQrLoginValue } from "@/lib/utils";

const DashboardPage = lazy(() => import("@/pages/DashboardPage"));
const TerminalPage = lazy(() => import("@/pages/TerminalPage"));
const HelpPage = lazy(() => import("@/pages/HelpPage"));
const QrScannerDialog = lazy(() => import("@/components/QrScannerDialog"));

const resolveBackendUrl = () => {
  const fallback = typeof window !== "undefined" ? window.location.origin : "http://localhost:8061";
  const raw = process.env.REACT_APP_BACKEND_URL;
  const rawTrimmed = typeof raw === "string" ? raw.trim() : "";
  if (typeof window !== "undefined") {
    const hostname = String(window.location.hostname || "").toLowerCase();
    const isLocalHost = hostname === "localhost" || hostname === "127.0.0.1";
    const isLocalEnv = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?/i.test(rawTrimmed);
    if (!isLocalHost && isLocalEnv) return fallback;
  }
  const picked = rawTrimmed ? rawTrimmed : fallback;
  const normalized = picked.endsWith("/") ? picked.slice(0, -1) : picked;
  if (!/^https?:\/\//i.test(normalized)) return fallback;
  return normalized;
};

const BACKEND_URL = resolveBackendUrl();
export const API = `${BACKEND_URL}/api`;
const COOKIE_CONSENT_KEY = "cookie_consent_v1";

export const resolveFontFamilyKey = (key) => {
  if (!key) return "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  if (typeof key === "string" && (key.includes(",") || key.includes("'") || key.includes('"'))) return key;
  if (key === "space_mono") return "'Space Mono', 'Courier New', monospace";
  if (key === "system") return "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  if (key === "monospace")
    return "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace";
  if (key === "space_grotesk") return "'Space Grotesk', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
  return `'${String(key).replaceAll("'", "\\'")}', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif`;
};

const normalizeColorChannel = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(255, Math.round(n)));
};

const normalizeAlpha = (value, fallback) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(0, Math.min(1, n));
};

const normalizeColor = (value, fallback) => {
  const v = value && typeof value === "object" ? value : null;
  return {
    r: normalizeColorChannel(v?.r, fallback.r),
    g: normalizeColorChannel(v?.g, fallback.g),
    b: normalizeColorChannel(v?.b, fallback.b),
    a: normalizeAlpha(v?.a, fallback.a),
  };
};

const rgbaToCss = (rgba) => {
  const c = normalizeColor(rgba, { r: 0, g: 0, b: 0, a: 1 });
  return `rgba(${c.r}, ${c.g}, ${c.b}, ${c.a})`;
};

const applyTypographyVars = (uiSettings) => {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  const globalFontSize = Number.isFinite(uiSettings?.global_font_size) ? uiSettings.global_font_size : 16;
  const terminalFontSize = Number.isFinite(uiSettings?.terminal_font_size) ? uiSettings.terminal_font_size : 14;
  const fileManagerFontSize = Number.isFinite(uiSettings?.file_manager_font_size) ? uiSettings.file_manager_font_size : 14;
  const modalFontSize = Number.isFinite(uiSettings?.modal_font_size) ? uiSettings.modal_font_size : globalFontSize;
  const macrosPanelFontSize = Number.isFinite(uiSettings?.macros_panel_font_size) ? uiSettings.macros_panel_font_size : 12;
  const macrosEditorFontSize = Number.isFinite(uiSettings?.macros_editor_font_size) ? uiSettings.macros_editor_font_size : 14;
  const pathBarCollapsedFontSize = Number.isFinite(uiSettings?.path_bar_collapsed_font_size) ? uiSettings.path_bar_collapsed_font_size : 11;
  const pathBarExpandedFontSize = Number.isFinite(uiSettings?.path_bar_expanded_font_size) ? uiSettings.path_bar_expanded_font_size : 14;

  const globalFontWeight = Number.isFinite(uiSettings?.global_font_weight) ? uiSettings.global_font_weight : 400;
  const terminalFontWeight = Number.isFinite(uiSettings?.terminal_font_weight) ? uiSettings.terminal_font_weight : 400;
  const fileManagerFontWeight = Number.isFinite(uiSettings?.file_manager_font_weight) ? uiSettings.file_manager_font_weight : 400;
  const modalFontWeight = Number.isFinite(uiSettings?.modal_font_weight) ? uiSettings.modal_font_weight : globalFontWeight;
  const macrosPanelFontWeight = Number.isFinite(uiSettings?.macros_panel_font_weight) ? uiSettings.macros_panel_font_weight : 400;
  const macrosEditorFontWeight = Number.isFinite(uiSettings?.macros_editor_font_weight) ? uiSettings.macros_editor_font_weight : 400;

  const textColor = normalizeColor(uiSettings?.text_color, { r: 255, g: 255, b: 255, a: 1 });
  const modalTextColor = normalizeColor(uiSettings?.modal_text_color, textColor);
  const macrosPanelTextColor = normalizeColor(uiSettings?.macros_panel_text_color, textColor);
  const macrosEditorTextColor = normalizeColor(uiSettings?.macros_editor_text_color, modalTextColor);
  const settingsModalTextColor = normalizeColor(uiSettings?.settings_modal_text_color, modalTextColor);
  const metricsModalTextColor = normalizeColor(uiSettings?.metrics_modal_text_color, modalTextColor);
  const blockBgColor = normalizeColor(uiSettings?.block_bg_color, { r: 255, g: 255, b: 255, a: 0.05 });
  const pageBgColor = normalizeColor(uiSettings?.page_bg_color, { r: 10, g: 14, b: 26, a: 1 });
  const modalBgColor = normalizeColor(uiSettings?.modal_bg_color, blockBgColor);
  const settingsModalBgColor = normalizeColor(uiSettings?.settings_modal_bg_color, modalBgColor);
  const metricsModalBgColor = normalizeColor(uiSettings?.metrics_modal_bg_color, modalBgColor);
  const macrosPanelBgColor = normalizeColor(uiSettings?.macros_panel_bg_color, { r: 0, g: 0, b: 0, a: 0.2 });
  const macrosEditorBgColor = normalizeColor(uiSettings?.macros_editor_bg_color, modalBgColor);
  const terminalBgColor = normalizeColor(uiSettings?.terminal_bg_color, { r: 0, g: 0, b: 0, a: 0.3 });
  const fileManagerBgColor = normalizeColor(uiSettings?.file_manager_bg_color, blockBgColor);
  const pathBarCollapsedBgColor = normalizeColor(uiSettings?.path_bar_collapsed_bg_color, blockBgColor);
  const pathBarCollapsedTextColor = normalizeColor(uiSettings?.path_bar_collapsed_text_color, { r: 255, g: 255, b: 255, a: 0.6 });
  const pathBarExpandedBgColor = normalizeColor(uiSettings?.path_bar_expanded_bg_color, blockBgColor);
  const pathBarExpandedTextColor = normalizeColor(uiSettings?.path_bar_expanded_text_color, { r: 255, g: 255, b: 255, a: 0.9 });

  const toPx = (value, fallback) => {
    const n = Number(value);
    if (!Number.isFinite(n) || n <= 0) return `${fallback}px`;
    return `${n}px`;
  };

  root.style.setProperty("--ui-root-font-size", toPx(globalFontSize, 16));
  root.style.setProperty("--ui-font-family", resolveFontFamilyKey(uiSettings?.global_font_family));
  root.style.setProperty("--terminal-font-family", resolveFontFamilyKey(uiSettings?.terminal_font_family));
  root.style.setProperty("--file-font-family", resolveFontFamilyKey(uiSettings?.file_manager_font_family));
  root.style.setProperty("--modal-font-family", resolveFontFamilyKey(uiSettings?.modal_font_family || uiSettings?.global_font_family));
  root.style.setProperty("--macros-panel-font-family", resolveFontFamilyKey(uiSettings?.macros_panel_font_family || uiSettings?.terminal_font_family));
  root.style.setProperty("--macros-editor-font-family", resolveFontFamilyKey(uiSettings?.macros_editor_font_family || uiSettings?.macros_panel_font_family || uiSettings?.terminal_font_family));
  root.style.setProperty("--terminal-font-size", toPx(terminalFontSize, 14));
  root.style.setProperty("--file-font-size", toPx(fileManagerFontSize, 14));
  root.style.setProperty("--modal-font-size", toPx(modalFontSize, globalFontSize));
  root.style.setProperty("--macros-panel-font-size", toPx(macrosPanelFontSize, 12));
  root.style.setProperty("--macros-editor-font-size", toPx(macrosEditorFontSize, 14));
  root.style.setProperty("--path-bar-collapsed-font-family", resolveFontFamilyKey(uiSettings?.path_bar_collapsed_font_family || uiSettings?.file_manager_font_family));
  root.style.setProperty("--path-bar-collapsed-font-size", toPx(pathBarCollapsedFontSize, 11));
  root.style.setProperty("--path-bar-expanded-font-family", resolveFontFamilyKey(uiSettings?.path_bar_expanded_font_family || uiSettings?.file_manager_font_family));
  root.style.setProperty("--path-bar-expanded-font-size", toPx(pathBarExpandedFontSize, 14));
  root.style.setProperty("--ui-font-weight", `${Math.max(100, Math.min(900, Math.round(globalFontWeight / 100) * 100))}`);
  root.style.setProperty("--terminal-font-weight", `${Math.max(100, Math.min(900, Math.round(terminalFontWeight / 100) * 100))}`);
  root.style.setProperty("--file-font-weight", `${Math.max(100, Math.min(900, Math.round(fileManagerFontWeight / 100) * 100))}`);
  root.style.setProperty("--modal-font-weight", `${Math.max(100, Math.min(900, Math.round(modalFontWeight / 100) * 100))}`);
  root.style.setProperty("--macros-panel-font-weight", `${Math.max(100, Math.min(900, Math.round(macrosPanelFontWeight / 100) * 100))}`);
  root.style.setProperty("--macros-editor-font-weight", `${Math.max(100, Math.min(900, Math.round(macrosEditorFontWeight / 100) * 100))}`);

  root.style.setProperty("--ui-text-r", `${Number.isFinite(textColor?.r) ? textColor.r : 255}`);
  root.style.setProperty("--ui-text-g", `${Number.isFinite(textColor?.g) ? textColor.g : 255}`);
  root.style.setProperty("--ui-text-b", `${Number.isFinite(textColor?.b) ? textColor.b : 255}`);
  root.style.setProperty("--ui-text-a", `${Number.isFinite(textColor?.a) ? textColor.a : 1}`);
  root.style.setProperty("--modal-text-r", `${Number.isFinite(modalTextColor?.r) ? modalTextColor.r : Number.isFinite(textColor?.r) ? textColor.r : 255}`);
  root.style.setProperty("--modal-text-g", `${Number.isFinite(modalTextColor?.g) ? modalTextColor.g : Number.isFinite(textColor?.g) ? textColor.g : 255}`);
  root.style.setProperty("--modal-text-b", `${Number.isFinite(modalTextColor?.b) ? modalTextColor.b : Number.isFinite(textColor?.b) ? textColor.b : 255}`);
  root.style.setProperty("--modal-text-a", `${Number.isFinite(modalTextColor?.a) ? modalTextColor.a : Number.isFinite(textColor?.a) ? textColor.a : 1}`);
  root.style.setProperty("--settings-modal-text-r", `${settingsModalTextColor.r}`);
  root.style.setProperty("--settings-modal-text-g", `${settingsModalTextColor.g}`);
  root.style.setProperty("--settings-modal-text-b", `${settingsModalTextColor.b}`);
  root.style.setProperty("--settings-modal-text-a", `${settingsModalTextColor.a}`);
  root.style.setProperty("--metrics-modal-text-r", `${metricsModalTextColor.r}`);
  root.style.setProperty("--metrics-modal-text-g", `${metricsModalTextColor.g}`);
  root.style.setProperty("--metrics-modal-text-b", `${metricsModalTextColor.b}`);
  root.style.setProperty("--metrics-modal-text-a", `${metricsModalTextColor.a}`);
  root.style.setProperty("--macros-panel-text-r", `${Number.isFinite(macrosPanelTextColor?.r) ? macrosPanelTextColor.r : Number.isFinite(textColor?.r) ? textColor.r : 255}`);
  root.style.setProperty("--macros-panel-text-g", `${Number.isFinite(macrosPanelTextColor?.g) ? macrosPanelTextColor.g : Number.isFinite(textColor?.g) ? textColor.g : 255}`);
  root.style.setProperty("--macros-panel-text-b", `${Number.isFinite(macrosPanelTextColor?.b) ? macrosPanelTextColor.b : Number.isFinite(textColor?.b) ? textColor.b : 255}`);
  root.style.setProperty("--macros-panel-text-a", `${Number.isFinite(macrosPanelTextColor?.a) ? macrosPanelTextColor.a : Number.isFinite(textColor?.a) ? textColor.a : 1}`);
  root.style.setProperty("--macros-editor-text-r", `${macrosEditorTextColor.r}`);
  root.style.setProperty("--macros-editor-text-g", `${macrosEditorTextColor.g}`);
  root.style.setProperty("--macros-editor-text-b", `${macrosEditorTextColor.b}`);
  root.style.setProperty("--macros-editor-text-a", `${macrosEditorTextColor.a}`);
  root.style.setProperty("--ui-block-bg-r", `${Number.isFinite(blockBgColor?.r) ? blockBgColor.r : 255}`);
  root.style.setProperty("--ui-block-bg-g", `${Number.isFinite(blockBgColor?.g) ? blockBgColor.g : 255}`);
  root.style.setProperty("--ui-block-bg-b", `${Number.isFinite(blockBgColor?.b) ? blockBgColor.b : 255}`);
  root.style.setProperty("--ui-block-bg-a", `${Number.isFinite(blockBgColor?.a) ? blockBgColor.a : 0.05}`);

  root.style.setProperty("--ui-page-bg-r", `${pageBgColor.r}`);
  root.style.setProperty("--ui-page-bg-g", `${pageBgColor.g}`);
  root.style.setProperty("--ui-page-bg-b", `${pageBgColor.b}`);
  root.style.setProperty("--ui-page-bg-a", `${pageBgColor.a}`);

  root.style.setProperty("--modal-bg-r", `${modalBgColor.r}`);
  root.style.setProperty("--modal-bg-g", `${modalBgColor.g}`);
  root.style.setProperty("--modal-bg-b", `${modalBgColor.b}`);
  root.style.setProperty("--modal-bg-a", `${modalBgColor.a}`);
  root.style.setProperty("--settings-modal-bg-r", `${settingsModalBgColor.r}`);
  root.style.setProperty("--settings-modal-bg-g", `${settingsModalBgColor.g}`);
  root.style.setProperty("--settings-modal-bg-b", `${settingsModalBgColor.b}`);
  root.style.setProperty("--settings-modal-bg-a", `${settingsModalBgColor.a}`);
  root.style.setProperty("--metrics-modal-bg-r", `${metricsModalBgColor.r}`);
  root.style.setProperty("--metrics-modal-bg-g", `${metricsModalBgColor.g}`);
  root.style.setProperty("--metrics-modal-bg-b", `${metricsModalBgColor.b}`);
  root.style.setProperty("--metrics-modal-bg-a", `${metricsModalBgColor.a}`);

  root.style.setProperty("--macros-panel-bg-r", `${macrosPanelBgColor.r}`);
  root.style.setProperty("--macros-panel-bg-g", `${macrosPanelBgColor.g}`);
  root.style.setProperty("--macros-panel-bg-b", `${macrosPanelBgColor.b}`);
  root.style.setProperty("--macros-panel-bg-a", `${macrosPanelBgColor.a}`);

  root.style.setProperty("--macros-editor-bg-r", `${macrosEditorBgColor.r}`);
  root.style.setProperty("--macros-editor-bg-g", `${macrosEditorBgColor.g}`);
  root.style.setProperty("--macros-editor-bg-b", `${macrosEditorBgColor.b}`);
  root.style.setProperty("--macros-editor-bg-a", `${macrosEditorBgColor.a}`);

  root.style.setProperty("--terminal-bg-r", `${terminalBgColor.r}`);
  root.style.setProperty("--terminal-bg-g", `${terminalBgColor.g}`);
  root.style.setProperty("--terminal-bg-b", `${terminalBgColor.b}`);
  root.style.setProperty("--terminal-bg-a", `${terminalBgColor.a}`);

  root.style.setProperty("--file-manager-bg-r", `${fileManagerBgColor.r}`);
  root.style.setProperty("--file-manager-bg-g", `${fileManagerBgColor.g}`);
  root.style.setProperty("--file-manager-bg-b", `${fileManagerBgColor.b}`);
  root.style.setProperty("--file-manager-bg-a", `${fileManagerBgColor.a}`);

  root.style.setProperty("--path-bar-collapsed-bg-r", `${pathBarCollapsedBgColor.r}`);
  root.style.setProperty("--path-bar-collapsed-bg-g", `${pathBarCollapsedBgColor.g}`);
  root.style.setProperty("--path-bar-collapsed-bg-b", `${pathBarCollapsedBgColor.b}`);
  root.style.setProperty("--path-bar-collapsed-bg-a", `${pathBarCollapsedBgColor.a}`);
  root.style.setProperty("--path-bar-collapsed-text-r", `${pathBarCollapsedTextColor.r}`);
  root.style.setProperty("--path-bar-collapsed-text-g", `${pathBarCollapsedTextColor.g}`);
  root.style.setProperty("--path-bar-collapsed-text-b", `${pathBarCollapsedTextColor.b}`);
  root.style.setProperty("--path-bar-collapsed-text-a", `${pathBarCollapsedTextColor.a}`);

  root.style.setProperty("--path-bar-expanded-bg-r", `${pathBarExpandedBgColor.r}`);
  root.style.setProperty("--path-bar-expanded-bg-g", `${pathBarExpandedBgColor.g}`);
  root.style.setProperty("--path-bar-expanded-bg-b", `${pathBarExpandedBgColor.b}`);
  root.style.setProperty("--path-bar-expanded-bg-a", `${pathBarExpandedBgColor.a}`);
  root.style.setProperty("--path-bar-expanded-text-r", `${pathBarExpandedTextColor.r}`);
  root.style.setProperty("--path-bar-expanded-text-g", `${pathBarExpandedTextColor.g}`);
  root.style.setProperty("--path-bar-expanded-text-b", `${pathBarExpandedTextColor.b}`);
  root.style.setProperty("--path-bar-expanded-text-a", `${pathBarExpandedTextColor.a}`);
};

const applyStyleOverrides = (uiSettings) => {
  if (typeof document === "undefined") return;
  const global =
    uiSettings?.style_overrides && typeof uiSettings.style_overrides === "object" && !Array.isArray(uiSettings.style_overrides)
      ? uiSettings.style_overrides
      : null;
  const byBlockRaw =
    uiSettings?.style_overrides_by_block &&
    typeof uiSettings.style_overrides_by_block === "object" &&
    !Array.isArray(uiSettings.style_overrides_by_block)
      ? uiSettings.style_overrides_by_block
      : null;
  const merged = global ? { ...global } : {};
  if (byBlockRaw) {
    const blockIds = Object.keys(byBlockRaw).sort();
    for (const blockId of blockIds) {
      const overrides = byBlockRaw?.[blockId];
      if (!overrides || typeof overrides !== "object" || Array.isArray(overrides)) continue;
      Object.assign(merged, overrides);
    }
  }
  let styleEl = document.getElementById("ui-style-overrides");
  if (!styleEl) {
    styleEl = document.createElement("style");
    styleEl.id = "ui-style-overrides";
    document.head.appendChild(styleEl);
  }
  document.head.appendChild(styleEl);

  if (!merged || Object.keys(merged).length === 0) {
    styleEl.textContent = "";
    return;
  }
  styleEl.textContent = buildOverridesCss(merged, resolveFontFamilyKey);
};

function App() {
  const [userId, setUserId] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [isCookieConsentOpen, setIsCookieConsentOpen] = useState(false);
  const [uiSettings, setUiSettings] = useState({
    show_sizes: true,
    show_created_date: true,
    file_manager_table_view: false,
    file_manager_grid_view: false,
    file_manager_grid_columns: 4,
    file_manager_show_popular: false,
    file_manager_popular: {},
    file_manager_sort_by: "name",
    file_manager_sort_dir: "asc",
    file_manager_path_bar_collapsed: false,
    remember_last_folders: true,
    terminal_mobile_view: "terminal",
    terminal_top_bar_collapsed: false,
    style_editor_popover_pos: null,
    style_overrides: {},
    style_overrides_by_block: {},
    pinned_folders: {},
    global_font_family: "space_grotesk",
    global_font_size: 16,
    global_font_weight: 400,
    terminal_font_family: "space_mono",
    terminal_font_size: 14,
    terminal_font_weight: 400,
    file_manager_font_family: "space_grotesk",
    file_manager_font_size: 14,
    file_manager_font_weight: 400,
    modal_font_family: "space_grotesk",
    modal_font_size: 16,
    modal_font_weight: 400,
    macros_panel_font_family: "space_mono",
    macros_panel_font_size: 12,
    macros_panel_font_weight: 400,
    macros_editor_font_family: "space_mono",
    macros_editor_font_size: 14,
    macros_editor_font_weight: 400,
    text_color: { r: 255, g: 255, b: 255, a: 1 },
    modal_text_color: { r: 255, g: 255, b: 255, a: 1 },
    macros_panel_text_color: { r: 255, g: 255, b: 255, a: 1 },
    macros_editor_text_color: { r: 255, g: 255, b: 255, a: 1 },
    settings_modal_text_color: { r: 255, g: 255, b: 255, a: 1 },
    metrics_modal_text_color: { r: 255, g: 255, b: 255, a: 1 },
    block_bg_color: { r: 255, g: 255, b: 255, a: 0.05 },
    page_bg_color: { r: 10, g: 14, b: 26, a: 1 },
    modal_bg_color: { r: 255, g: 255, b: 255, a: 0.06 },
    settings_modal_bg_color: { r: 255, g: 255, b: 255, a: 0.06 },
    metrics_modal_bg_color: { r: 255, g: 255, b: 255, a: 0.06 },
    macros_panel_bg_color: { r: 0, g: 0, b: 0, a: 0.2 },
    macros_editor_bg_color: { r: 0, g: 0, b: 0, a: 0.25 },
    terminal_bg_color: { r: 0, g: 0, b: 0, a: 0.3 },
    file_manager_bg_color: { r: 255, g: 255, b: 255, a: 0.05 },
    path_bar_collapsed_bg_color: { r: 255, g: 255, b: 255, a: 0.05 },
    path_bar_collapsed_text_color: { r: 255, g: 255, b: 255, a: 0.6 },
    path_bar_collapsed_font_family: "space_grotesk",
    path_bar_collapsed_font_size: 11,
    path_bar_expanded_bg_color: { r: 255, g: 255, b: 255, a: 0.05 },
    path_bar_expanded_text_color: { r: 255, g: 255, b: 255, a: 0.9 },
    path_bar_expanded_font_family: "space_grotesk",
    path_bar_expanded_font_size: 14,
  });
  const [uiSettingsLoaded, setUiSettingsLoaded] = useState(false);
  const didMigrateLegacyUiStorageRef = useRef(false);

  const refreshAuth = async () => {
    try {
      const resp = await axios.get(`${API}/auth/me`, { withCredentials: true });
      const nextUserId = typeof resp?.data?.user_id === "string" ? resp.data.user_id : null;
      setUserId(nextUserId);
      return nextUserId;
    } catch {
      setUserId(null);
      return null;
    } finally {
      setAuthChecked(true);
    }
  };

  const handleLogin = async () => {
    await refreshAuth();
  };

  useEffect(() => {
    try {
      const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
      setIsCookieConsentOpen(stored !== "1");
    } catch {
      setIsCookieConsentOpen(true);
    }
  }, []);

  const acceptCookies = () => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, "1");
    } catch {}
    setIsCookieConsentOpen(false);
  };

  const handleLogout = () => {
    axios.post(`${API}/auth/logout`, null, { withCredentials: true }).catch(() => {});
    setUserId(null);
    setAuthChecked(true);
  };

  useEffect(() => {
    axios.defaults.withCredentials = true;
    refreshAuth();
    const requestInterceptorId = axios.interceptors.request.use((config) => {
      if (config && typeof config === "object" && config.withCredentials === undefined) {
        return { ...config, withCredentials: true };
      }
      return config;
    });

    const responseInterceptorId = axios.interceptors.response.use(
      (resp) => resp,
      (error) => {
        const status = error?.response?.status;
        const authReason = error?.response?.headers?.["x-auth-reason"];
        const detail = error?.response?.data?.detail;
        const shouldClearAuth =
          status === 401 && (Boolean(authReason) || detail === "Token expired" || detail === "Session revoked");
        if (shouldClearAuth) {
          axios.post(`${API}/auth/logout`, null, { withCredentials: true }).catch(() => {});
          setUserId(null);
          setAuthChecked(true);
          toast.error("Сессия истекла. Войдите снова.");
        }
        return Promise.reject(error);
      },
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptorId);
      axios.interceptors.response.eject(responseInterceptorId);
    };
  }, []);

  useEffect(() => {
    applyTypographyVars(uiSettings);
    applyStyleOverrides(uiSettings);
  }, [uiSettings]);

  useEffect(() => {
    if (!userId) return;
    setUiSettingsLoaded(false);
    (async () => {
      try {
        const resp = await axios.get(`${API}/ui/settings`, { withCredentials: true });
        if (resp?.data) setUiSettings((prev) => ({ ...prev, ...resp.data }));
      } catch {} finally {
        setUiSettingsLoaded(true);
      }
    })();
  }, [userId]);

  const saveUiSettings = async (updates) => {
    const payload = updates && typeof updates === "object" ? updates : {};
    setUiSettings((prev) => ({ ...(prev || {}), ...payload }));
    if (!userId) return true;
    try {
      const resp = await axios.post(`${API}/ui/settings`, payload, { withCredentials: true });
      if (resp?.data && typeof resp.data === "object") setUiSettings((prev) => ({ ...(prev || {}), ...resp.data }));
      return true;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    if (!userId) return;
    if (!uiSettingsLoaded) return;
    if (didMigrateLegacyUiStorageRef.current) return;
    didMigrateLegacyUiStorageRef.current = true;

    const updates = {};
    try {
      const mobileView = localStorage.getItem("ui:terminalMobileView");
      if (mobileView === "files" || mobileView === "terminal") updates.terminal_mobile_view = mobileView;
    } catch {}
    try {
      const collapsed = localStorage.getItem("ui:terminalTopBarCollapsed");
      if (collapsed === "1" || collapsed === "0") updates.terminal_top_bar_collapsed = collapsed === "1";
    } catch {}
    try {
      const collapsed = localStorage.getItem("ui:file_manager:path_bar_collapsed");
      if (collapsed === "1" || collapsed === "0") updates.file_manager_path_bar_collapsed = collapsed === "1";
    } catch {}
    try {
      const raw = localStorage.getItem("ui:styleEditorPopoverPos");
      const parsed = raw ? JSON.parse(raw) : null;
      const x = Number(parsed?.x);
      const y = Number(parsed?.y);
      if (Number.isFinite(x) && Number.isFinite(y)) updates.style_editor_popover_pos = { x, y };
    } catch {}

    if (Object.keys(updates).length > 0) {
      saveUiSettings(updates).catch(() => {});
    }

    try {
      localStorage.removeItem("ui:terminalMobileView");
      localStorage.removeItem("ui:terminalTopBarCollapsed");
      localStorage.removeItem("ui:file_manager:path_bar_collapsed");
      localStorage.removeItem("ui:styleEditorPopoverPos");
    } catch {}
  }, [saveUiSettings, uiSettingsLoaded, userId]);

  const LoginRoute = () => {
    const location = useLocation();
    const next = new URLSearchParams(location.search).get("next");
    if (!authChecked) return <div className="min-h-[100dvh] ui-page-bg" />;
    if (!userId) return <LoginPage onLogin={handleLogin} />;
    return <Navigate to={next || "/dashboard"} replace />;
  };

  const ShareImportPage = () => {
    const location = useLocation();
    const params = useParams();
    const shareToken = params.token;
    const [isImporting, setIsImporting] = useState(false);
    const [isDone, setIsDone] = useState(false);

    useEffect(() => {
      if (!userId || !shareToken) return;
      if (isImporting || isDone) return;
      setIsImporting(true);
      (async () => {
        try {
          await axios.post(
            `${API}/connections/share/import`,
            { token: shareToken },
            { withCredentials: true },
          );
          toast.success("Подключение добавлено");
          setIsDone(true);
        } catch (error) {
          toast.error(error.response?.data?.detail || "Не удалось импортировать подключение");
          setIsDone(true);
        } finally {
          setIsImporting(false);
        }
      })();
    }, [isDone, isImporting, shareToken, userId]);

    if (authChecked && !userId) {
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
    }
    if (isDone) return <Navigate to="/dashboard" replace />;

    return (
      <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
        Импорт подключения...
      </div>
    );
  };

  const QrApprovePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const search = new URLSearchParams(location.search);
    const qrId = search.get("qr_id");
    const code = search.get("code");
    const [status, setStatus] = useState(qrId && code ? "pending" : "scan");
    const [error, setError] = useState(null);
    const [isScanOpen, setIsScanOpen] = useState(false);

    const handleScanResult = (value) => {
      const parsed = parseQrLoginValue(value);
      if (!parsed?.qr_id || !parsed?.code) {
        toast.error("Некорректный QR");
        return;
      }
      navigate(`/qr?qr_id=${encodeURIComponent(parsed.qr_id)}&code=${encodeURIComponent(parsed.code)}`, { replace: true });
    };

    useEffect(() => {
      if (!userId) return;
      if (!qrId || !code) {
        setStatus("scan");
        setError(null);
        return;
      }
      let isActive = true;
      setStatus("approving");
      setError(null);
      (async () => {
        try {
          const resp = await axios.post(
            `${API}/auth/qr/approve`,
            { qr_id: qrId, code },
            { withCredentials: true },
          );
          if (!isActive) return;
          setStatus(resp?.data?.status === "approved" ? "approved" : "done");
        } catch (e) {
          if (!isActive) return;
          setStatus("error");
          setError(e?.response?.data?.detail || "Не удалось подтвердить вход");
        }
      })();
      return () => {
        isActive = false;
      };
    }, [code, qrId, userId]);

    if (authChecked && !userId) {
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
    }

    if (status === "scan") {
      return (
        <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex flex-col items-center justify-center gap-4">
          <div className="text-lg font-semibold">Подтвердить вход по QR</div>
          <div className="text-sm text-white/70 text-center max-w-md">
            Нажмите «Сканировать», разрешите доступ к камере и наведите её на QR-код с экрана устройства, где открыт вход.
          </div>
          <Button
            type="button"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            onClick={() => setIsScanOpen(true)}
          >
            Сканировать QR
          </Button>
          <Suspense fallback={null}>
            <QrScannerDialog
              open={isScanOpen}
              onOpenChange={setIsScanOpen}
              title="Сканировать QR для подтверждения входа"
              description="Код можно также вставить вручную, если камера недоступна."
              confirmLabel="Подтвердить"
              onResult={handleScanResult}
            />
          </Suspense>
        </div>
      );
    }

    if (status === "approved") {
      return (
        <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
          Вход подтвержден
        </div>
      );
    }

    if (status === "error") {
      return (
        <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
          {error || "Ошибка"}
        </div>
      );
    }

    return (
      <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
        Подтверждение входа...
      </div>
    );
  };

  const OneTimeExchangePage = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const search = new URLSearchParams(location.search);
    const code = search.get("code");
    const [status, setStatus] = useState("pending");
    const [error, setError] = useState(null);
    const [isScanOpen, setIsScanOpen] = useState(false);

    const openScan = () => setIsScanOpen(true);

    const handleScanResult = (value) => {
      const raw = String(value || "").trim();
      if (!raw) return;
      try {
        const url = new URL(raw, window.location.origin);
        const codeValue = url.searchParams.get("code");
        if (codeValue) {
          navigate(`/one-time?code=${encodeURIComponent(codeValue)}`, { replace: true });
          return;
        }
      } catch {}
      navigate(`/one-time?code=${encodeURIComponent(raw)}`, { replace: true });
    };

    useEffect(() => {
      if (!code) {
        setStatus("scan");
        setError(null);
        return;
      }
      let isActive = true;
      setStatus("exchanging");
      setError(null);
      (async () => {
        try {
          const resp = await axios.post(`${API}/auth/one-time/exchange`, { code }, { withCredentials: true });
          if (!isActive) return;
          if (!resp?.data?.ok) {
            setStatus("error");
            setError("Не удалось войти");
            return;
          }
          await handleLogin();
          setStatus("done");
        } catch (e) {
          if (!isActive) return;
          setStatus("error");
          setError(e?.response?.data?.detail || "Не удалось войти");
        }
      })();
      return () => {
        isActive = false;
      };
    }, [code]);

    if (status === "done") return <Navigate to="/dashboard" replace />;
    if (status === "scan") {
      return (
        <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex flex-col items-center justify-center gap-4">
          <div className="text-lg font-semibold">Вход по QR</div>
          <div className="text-sm text-white/70 text-center max-w-md">
            Нажмите «Сканировать», разрешите доступ к камере и наведите её на QR-код.
          </div>
          <Button
            type="button"
            className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
            onClick={openScan}
          >
            Сканировать QR
          </Button>
          <Suspense fallback={null}>
            <QrScannerDialog
              open={isScanOpen}
              onOpenChange={setIsScanOpen}
              title="Сканировать QR для входа"
              description="Код можно также вставить вручную, если камера недоступна."
              confirmLabel="Войти"
              onResult={handleScanResult}
            />
          </Suspense>
        </div>
      );
    }
    if (status === "error") {
      return (
        <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
          {error || "Ошибка"}
        </div>
      );
    }
    return (
      <div className="min-h-[100dvh] ui-page-bg p-6 text-white flex items-center justify-center">
        Вход по QR...
      </div>
    );
  };

  return (
    <div className="App">
      <Toaster position="bottom-center" />
      <BrowserRouter basename={getPublicBasePath() || undefined}>
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route 
            path="/dashboard" 
            element={
              !authChecked ? (
                <div className="min-h-[100dvh] ui-page-bg" />
              ) : userId ? (
                <Suspense fallback={<div className="min-h-[100dvh] ui-page-bg" />}>
                  <DashboardPage onLogout={handleLogout} uiSettings={uiSettings} onSaveUiSettings={saveUiSettings} />
                </Suspense>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route 
            path="/terminal/:connectionId" 
            element={
              !authChecked ? (
                <div className="min-h-[100dvh] ui-page-bg" />
              ) : userId ? (
                <Suspense fallback={<div className="min-h-[100dvh] ui-page-bg" />}>
                  <TerminalPage
                    onLogout={handleLogout}
                    uiSettings={uiSettings}
                    uiSettingsLoaded={uiSettingsLoaded}
                    onSaveUiSettings={saveUiSettings}
                  />
                </Suspense>
              ) : (
                <Navigate to="/login" />
              )
            } 
          />
          <Route
            path="/help"
            element={
              !authChecked ? (
                <div className="min-h-[100dvh] ui-page-bg" />
              ) : userId ? (
                <Suspense fallback={<div className="min-h-[100dvh] ui-page-bg" />}>
                  <HelpPage onLogout={handleLogout} />
                </Suspense>
              ) : (
                <Navigate to="/login" />
              )
            }
          />
          <Route path="/qr" element={<QrApprovePage />} />
          <Route path="/one-time" element={<OneTimeExchangePage />} />
          <Route path="/share/:token" element={<ShareImportPage />} />
          <Route
            path="/"
            element={
              !authChecked ? <div className="min-h-[100dvh] ui-page-bg" /> : <Navigate to={userId ? "/dashboard" : "/login"} />
            }
          />
        </Routes>
      </BrowserRouter>
      {isCookieConsentOpen && (
        <div className="fixed bottom-4 left-4 right-4 z-50">
          <div className="max-w-3xl mx-auto rounded-xl border border-white/10 bg-black/70 backdrop-blur px-4 py-3 text-white flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="text-sm text-white/80">
              Этот сайт использует cookies для авторизации и сохранения настроек интерфейса.
            </div>
            <Button
              type="button"
              className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 shrink-0"
              onClick={acceptCookies}
            >
              Понятно
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
